/**
 * Validation findings, shaped like the diagnostics the stream sends.
 *
 * The panel already renders `simulation_diagnostic` frames, and a finding from
 * `POST /amhs/validate` says the same kind of thing about the same layout — an
 * unroutable hop reads identically whether the pathfinder hit it mid-run or the
 * validator found it from the request. Reusing the frame shape keeps one sink
 * for both instead of a second list that says the same thing before Start.
 */

import type {
  AmhsRequestValidation,
  AmhsRequestValidationIssue,
  AmhsSimulationDiagnosticFrame,
} from '../../types/amhsStream';

function diagnosticId(issue: AmhsRequestValidationIssue): string {
  const related = issue.related ?? {};
  const detail = Object.keys(related)
    .sort()
    .map((key) => `${key}=${String(related[key])}`)
    .join(',');
  return ['preflight', issue.code, issue.path, detail].filter(Boolean).join(':');
}

export function toPreflightDiagnostics(
  validation: AmhsRequestValidation,
  context: { fabId: string }
): AmhsSimulationDiagnosticFrame[] {
  const issues = validation.errors.map((issue) => ({ issue, fatal: true }));
  const byId = new Map<string, AmhsSimulationDiagnosticFrame>();
  for (const { issue, fatal } of issues) {
    const id = diagnosticId(issue);
    const existing = byId.get(id);
    if (existing) {
      existing.occurrences = (existing.occurrences ?? 1) + 1;
      continue;
    }
    byId.set(id, {
      type: 'simulation_diagnostic',
      // No job exists yet, so there is no stream to attribute this to and no
      // sim clock to date it by.
      streamId: '',
      fabId: context.fabId,
      simTimeS: 0,
      diagnosticId: id,
      severity: fatal ? 'error' : 'warning',
      code: issue.code,
      phase: 'preflight',
      fatal,
      message: issue.message,
      suggestion: issue.suggestion,
      related: issue.related,
      occurrences: 1,
    });
  }
  return [...byId.values()];
}

function isBreakingDiagnostic(row: AmhsSimulationDiagnosticFrame): boolean {
  return row.fatal || row.severity === 'error';
}

/**
 * The floating panel shows blocking findings only. Validate API warnings such as
 * connectivity gaps are advisory and stay off the UI until they become errors.
 */
export function diagnosticsForDisplay(
  findings: AmhsSimulationDiagnosticFrame[]
): AmhsSimulationDiagnosticFrame[] {
  return findings.filter(isBreakingDiagnostic);
}
